import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import {
  BrainCircuit, Loader2, TrendingUp, TrendingDown, Minus, Lightbulb, Sparkles,
  CalendarClock, BedDouble, Euro, Ban, Gauge, ArrowRight, Upload, SlidersHorizontal,
  Target, CheckCircle2, AlertTriangle, Activity, BookOpen,
} from "lucide-react";

const OUTCOME_META = {
  excellent: { color: "#16a34a", it: "Ottime", en: "Excellent" },
  margin_lost: { color: "#b45309", it: "Margine perso", en: "Margin lost" },
  too_high: { color: "#ef4444", it: "Prezzo troppo alto", en: "Price too high" },
  could_push: { color: "#6366f1", it: "Si poteva osare", en: "Could push more" },
};

const levelColor = (lvl) => (lvl === "mature" ? "#16a34a" : lvl === "growing" ? "#b45309" : "#94a3b8");

function Gauge100({ value, level, L }) {
  const r = 52, c = 2 * Math.PI * r, off = c * (1 - (value || 0) / 100);
  const col = levelColor(level);
  return (
    <div className="relative w-[130px] h-[130px] shrink-0" data-testid="ail-gauge">
      <svg width="130" height="130" className="-rotate-90">
        <circle cx="65" cy="65" r={r} fill="none" stroke="currentColor" strokeWidth="10" className="text-muted/30" />
        <circle cx="65" cy="65" r={r} fill="none" stroke={col} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-data text-3xl font-bold" style={{ color: col }} data-testid="ail-reliability">{value}%</span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{L("affidabilità", "reliability")}</span>
      </div>
    </div>
  );
}

const Metric = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-muted/40 rounded-lg p-3.5" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1"><Icon className="w-3.5 h-3.5" />{label}</div>
    <div className="font-data text-xl font-semibold">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

export default function AILearning({ propId, lang = "it" }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propId) return;
    setLoading(true);
    try { const { data } = await api.get(`/properties/${propId}/ai-learning?lang=${lang}`); setD(data); }
    catch { setD(null); } finally { setLoading(false); }
  }, [propId, lang]);
  useEffect(() => { load(); }, [load]);

  const TrendIcon = d?.price_learning?.trend === "up" ? TrendingUp : d?.price_learning?.trend === "down" ? TrendingDown : Minus;
  const pl = d?.price_learning || {};
  const rz = d?.realized || {};
  const drivers = d?.reliability_drivers || {};

  const DRIVER_LABELS = {
    price_decisions: L("Decisioni di prezzo", "Price decisions"),
    bookings: L("Prenotazioni", "Bookings"),
    history_months: L("Mesi di storico", "Months of history"),
    data_completeness: L("Completezza dati", "Data completeness"),
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border-2 border-primary/25 rounded-lg p-6" data-testid="mp-ai-learning">
      <div className="flex items-center gap-2 mb-1"><BrainCircuit className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">AI Learning</h2></div>
      <p className="text-sm text-muted-foreground mb-5">{L("Il sistema apprende dal comportamento reale della tua struttura e personalizza progressivamente il prezzo consigliato.", "The system learns from your property's real behaviour and progressively personalises the recommended price.")}</p>

      {loading ? (
        <div className="py-12 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{L("Analizzo l'apprendimento…", "Analysing learning…")}</div>
      ) : !d ? (
        <p className="text-sm text-muted-foreground">{L("Dati non disponibili.", "Data unavailable.")}</p>
      ) : (
        <div className="space-y-6">
          {/* reliability + drivers */}
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <Gauge100 value={d.reliability} level={d.reliability_level} L={L} />
            <div className="flex-1 w-full">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold">{L("Livello di apprendimento", "Learning level")}:</span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full" data-testid="ail-level"
                  style={{ backgroundColor: `color-mix(in srgb, ${levelColor(d.reliability_level)} 16%, transparent)`, color: levelColor(d.reliability_level) }}>
                  {d.reliability_level_label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                {Object.entries(drivers).map(([k, v]) => (
                  <div key={k} data-testid={`ail-driver-${k}`}>
                    <div className="flex justify-between text-[11px] mb-1"><span className="text-muted-foreground">{DRIVER_LABELS[k] || k}</span><span className="font-data font-semibold">{v.value}</span></div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${v.score}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Experience Learning — outcome analysis + confidence calibration */}
          {d.experience && d.experience.matched > 0 && (
            <div className="rounded-lg border-2 border-primary/20 bg-primary/[0.04] p-5" data-testid="ail-experience">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-primary" />
                <h3 className="text-sm font-semibold">{L("Esperienza: come stanno andando i consigli", "Experience: how the recommendations are doing")}</h3>
              </div>
              {d.experience.headline && (
                <div className="flex items-start gap-2 mb-4 rounded-md bg-card border border-border px-3 py-2.5" data-testid="ail-accuracy-headline">
                  {(d.experience.weekly_delta || 0) > 0 ? <TrendingUp className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <Activity className="w-5 h-5 text-primary shrink-0 mt-0.5" />}
                  <p className="text-sm font-medium">{d.experience.headline}</p>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-card rounded-lg p-3 border border-border text-center" data-testid="ail-accuracy">
                  <div className="font-data text-2xl font-bold text-primary">{d.experience.accuracy != null ? `${d.experience.accuracy}%` : "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{L("Accuratezza", "Accuracy")}</div>
                </div>
                {Object.entries(OUTCOME_META).map(([k, m]) => (
                  <div key={k} className="bg-card rounded-lg p-3 border border-border text-center" data-testid={`ail-outcome-${k}`}>
                    <div className="font-data text-2xl font-bold" style={{ color: m.color }}>{(d.experience.results || {})[k] || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{L(m.it, m.en)}</div>
                  </div>
                ))}
              </div>
              {d.experience.confidence_calibration?.length > 0 && (
                <div data-testid="ail-confidence-calibration">
                  <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Calibrazione della sicurezza — quando l'AI era sicura, aveva ragione?", "Confidence calibration — when the AI was sure, was it right?")}</p>
                  <div className="space-y-2">
                    {d.experience.confidence_calibration.map((c) => (
                      <div key={c.bucket} className="flex items-center gap-3" data-testid={`ail-conf-${c.bucket}`}>
                        <span className="text-xs w-40 shrink-0 text-muted-foreground">{c.label}</span>
                        <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${c.correct_rate || 0}%`, backgroundColor: (c.correct_rate || 0) >= 66 ? "#16a34a" : (c.correct_rate || 0) >= 40 ? "#b45309" : "#ef4444" }} />
                        </div>
                        <span className="font-data text-sm font-semibold w-24 text-right">{c.correct_rate != null ? `${c.correct_rate}%` : "—"} <span className="text-[11px] text-muted-foreground font-normal">({c.samples})</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Learning Diary — the AI explains what it got wrong and how it self-corrected */}
          {d.experience?.diary && (d.experience.diary.summary || d.experience.diary.entries?.length > 0) && (
            <div className="rounded-lg border border-border p-5" data-testid="ail-diary">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-5 h-5 text-primary" />
                <h3 className="text-sm font-semibold">{L("Diario di apprendimento", "Learning diary")}</h3>
              </div>
              {d.experience.diary.summary && (
                <p className="text-sm bg-muted/40 rounded-md px-3 py-2.5 mb-3" data-testid="ail-diary-summary">{d.experience.diary.summary}</p>
              )}
              {d.experience.diary.adjustments?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="ail-diary-adjustments">
                  {d.experience.diary.adjustments.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary">
                      {a.label} <b className="font-data" style={{ color: a.delta_pct >= 0 ? "#16a34a" : "#ef4444" }}>{a.delta_pct > 0 ? "+" : ""}{a.delta_pct}%</b>
                    </span>
                  ))}
                </div>
              )}
              {d.experience.diary.entries?.length > 0 && (
                <div className="space-y-2">
                  {d.experience.diary.entries.map((e, i) => {
                    const m = OUTCOME_META[e.result] || { color: "#64748b", it: e.result, en: e.result };
                    return (
                      <div key={i} className="flex items-start gap-3 rounded-md bg-muted/30 px-3 py-2.5" data-testid={`ail-diary-entry-${i}`}>
                        <span className="mt-0.5 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: m.color }} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between flex-wrap gap-x-3">
                            <span className="text-sm font-medium">{e.stay_label}</span>
                            <span className="text-xs text-muted-foreground font-data">
                              {L("Consigliato", "Recommended")} €{e.recommended}{e.applied_real != null ? ` · ${L("venduto", "sold")} €${Math.round(e.applied_real)}` : ` · ${L("invenduto", "unsold")}`}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{e.verdict}</p>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                          style={{ backgroundColor: `color-mix(in srgb, ${m.color} 15%, transparent)`, color: m.color }}>{L(m.it, m.en)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!d.has_data ? (
            <div className="rounded-lg border border-dashed border-border p-5 text-center" data-testid="ail-empty">
              <Sparkles className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-4">{L("Ancora nessun dato reale. Importa le tue prenotazioni e registra i prezzi applicati: il sistema inizierà a personalizzare il prezzo per la tua struttura.", "No real data yet. Import your reservations and log the prices you apply: the system will start personalising the price for your property.")}</p>
              <button data-testid="ail-import-btn" onClick={() => nav("/import")} className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline">
                <Upload className="w-4 h-4" />{L("Importa prenotazioni", "Import reservations")}<ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <>
              {/* learned metrics */}
              <div>
                <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">{L("Cosa ha imparato", "What it learned")}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric testid="ail-adr" icon={Euro} label={L("ADR realizzato", "Realized ADR")} value={rz.realized_adr != null ? `€${rz.realized_adr}` : "—"} sub={rz.revpar != null ? `RevPAR ~€${rz.revpar}` : ""} />
                  <Metric testid="ail-occ" icon={Gauge} label={L("Occupazione media", "Avg occupancy")} value={rz.avg_occupancy != null ? `${rz.avg_occupancy}%` : "—"} sub={rz.realized_revenue != null ? L(`Ricavi €${rz.realized_revenue}`, `Revenue €${rz.realized_revenue}`) : ""} />
                  <Metric testid="ail-lead" icon={CalendarClock} label={L("Lead time medio", "Avg lead time")} value={rz.avg_lead_time != null ? L(`${Math.round(rz.avg_lead_time)} gg`, `${Math.round(rz.avg_lead_time)} d`) : "—"} sub={rz.avg_los != null ? L(`Soggiorno ${rz.avg_los} notti`, `Stay ${rz.avg_los} nights`) : ""} />
                  <Metric testid="ail-cancel" icon={Ban} label={L("Cancellazioni", "Cancellations")} value={rz.cancellation_rate != null ? `${Math.round(rz.cancellation_rate * 100)}%` : "—"} sub={rz.total_bookings ? L(`${rz.active_bookings}/${rz.total_bookings} attive`, `${rz.active_bookings}/${rz.total_bookings} active`) : ""} />
                </div>
              </div>

              {/* price-decision learning */}
              {pl.evaluated > 0 && (
                <div className="rounded-lg bg-primary/5 border border-primary/20 p-4" data-testid="ail-price-learning">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                    <span className="inline-flex items-center gap-1.5 font-semibold"><TrendIcon className="w-4 h-4 text-primary" />{L("Personalizzazione prezzo", "Price personalisation")}: <b className="font-data" style={{ color: pl.personalization_pct >= 0 ? "#16a34a" : "#ef4444" }}>{pl.personalization_pct > 0 ? "+" : ""}{pl.personalization_pct}%</b></span>
                    <span className="text-muted-foreground">{L("Decisioni valutate", "Evaluated decisions")}: <b className="text-foreground font-data">{pl.evaluated}</b></span>
                    {pl.acceptance_rate != null && <span className="text-muted-foreground">{L("Accettazione", "Acceptance")}: <b className="text-foreground font-data">{Math.round(pl.acceptance_rate * 100)}%</b></span>}
                  </div>
                </div>
              )}

              {/* channels + room types */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {d.channels?.length > 0 && (
                  <div data-testid="ail-channels">
                    <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">{L("Canali di vendita", "Sales channels")}</h3>
                    <div className="space-y-2.5">
                      {d.channels.map((c, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-xs mb-1"><span>{c.channel}</span><span className="font-data font-semibold">{c.pct}% · €{c.revenue}</span></div>
                          <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${c.pct}%` }} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {d.room_types?.length > 0 && (
                  <div data-testid="ail-room-types">
                    <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">{L("Tipologie camera", "Room types")}</h3>
                    <div className="space-y-2">
                      {d.room_types.map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-sm rounded-md bg-muted/40 px-3 py-2">
                          <span className="inline-flex items-center gap-2"><BedDouble className="w-4 h-4 text-muted-foreground" />{r.room_type}</span>
                          <span className="text-muted-foreground text-xs">{L(`${r.bookings} pren.`, `${r.bookings} bk`)} · <b className="text-foreground font-data">€{r.adr}</b> ADR</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* personalized factors */}
              {d.personalized_factors?.length > 0 && (
                <div data-testid="ail-factors">
                  <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2 inline-flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5" />{L("Fattori personalizzati per te", "Factors personalised for you")}</h3>
                  <div className="flex flex-wrap gap-2">
                    {d.personalized_factors.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary">
                        {f.label} <b className="font-data" style={{ color: f.delta_pct >= 0 ? "#16a34a" : "#ef4444" }}>{f.delta_pct > 0 ? "+" : ""}{f.delta_pct}%</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* insights */}
              {d.insights?.length > 0 && (
                <div data-testid="ail-insights">
                  <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2 inline-flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" />{L("Conoscenze apprese", "Learned insights")}</h3>
                  <ul className="space-y-1.5">
                    {d.insights.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{s}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* how it influences pricing */}
          {d.pricing_influence?.length > 0 && (
            <div className="pt-4 border-t border-border" data-testid="ail-influence">
              <h3 className="text-xs uppercase tracking-widest font-bold text-primary mb-2">{L("Come influenza il prezzo", "How it influences pricing")}</h3>
              <ul className="space-y-1.5">
                {d.pricing_influence.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm"><TrendingUp className="w-4 h-4 text-primary shrink-0 mt-0.5" />{s}</li>)}
              </ul>
            </div>
          )}

          {/* next steps to improve learning */}
          {d.next_steps?.length > 0 && (
            <div className="rounded-lg bg-muted/30 p-4" data-testid="ail-next-steps">
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Per migliorare l'apprendimento", "To improve learning")}</p>
              <ul className="space-y-1.5">
                {d.next_steps.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><ArrowRight className="w-4 h-4 text-primary shrink-0 mt-0.5" />{s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
